<template>
  <!-- Items grouped by the shop they were ordered from (locked answer L-C):
       «{shop} · بولشير {rate}% · {earned}», then each line with its quantity,
       unit price and total, and a «دفعة نقدية» chip on a line paid in cash at
       the shop. Manual lines close the list as one group. -->
  <section class="cis" data-block="items">
    <div class="cis-heading">
      <span>{{ $t('box.totals.account.items.heading') }}</span>
      <span class="cis-count">{{ lineCount }}</span>
    </div>

    <div
      v-for="(group, gIndex) in decoratedGroups"
      :key="group.supplierId ?? `manual-${gIndex}`"
      class="cis-group"
      data-row="group"
    >
      <div class="cis-group-header" data-row="group-header">{{ group.headerText }}</div>

      <div
        v-for="{ line, item, issue } in group.decoratedLines"
        :key="line.orderBoxItemId"
        class="cis-line"
        data-row="line"
      >
        <div class="cis-line-main">
          <div class="cis-line-name">{{ line.name || (item ? itemDisplayName(item) : '') }}</div>
          <div class="cis-line-sub">
            <bdi dir="ltr" class="cis-line-qty">{{ qtyTimesUnit(line) }}</bdi>
            <span v-if="line.cashAtShopCents > 0" class="cis-cash-chip" data-row="cash-chip">
              {{ $t('box.totals.account.items.cashChip', { amount: formatAmount(line.cashAtShopCents) }) }}
            </span>
          </div>
        </div>
        <div class="cis-line-after">
          <!-- The per-line issue chips are load-bearing (same presentation as
               BoxTotalsBreakdown): a routable issue opens its /issues/:id/
               card, being-handled and out-of-stock read as calm pills. -->
          <decision-chip
            v-if="issue?.chipVariant"
            interactive
            :variant="issue.chipVariant"
            :issue-id="issue.issueId"
            :item-name="line.name || itemDisplayName(item)"
          ></decision-chip>
          <turn-pill
            v-else-if="issue?.kind === ISSUE_PRESENTATION.BEING_HANDLED"
            variant="handled"
            :announce="false"
          ></turn-pill>
          <turn-pill
            v-else-if="issue?.kind === ISSUE_PRESENTATION.OUT_OF_STOCK"
            variant="oos"
            :announce="false"
          ></turn-pill>
          <span class="cis-line-total">{{ formatAmount(line.lineCents) }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import DecisionChip from '@/components/collection/DecisionChip.vue';
import TurnPill from '@/components/collection/TurnPill.vue';
import { useCurrency } from '@/composables/useCurrency.js';
import {
  classifyItemIssue,
  issueChipVariant,
  ISSUE_PRESENTATION,
} from '@/composables/useItemIssuePresentation.js';
import { orderedItemGroups } from '@/utils/cartonAccount.js';
import { itemDisplayName } from '@/utils/orderBoxItemDisplay.js';

const props = defineProps({
  // The order box's `cartonAccount`.
  account: {
    type: Object,
    required: true,
  },
  // The box's OrderBoxItem records, for each line's issue chip. Lines are
  // matched by `orderBoxItemId`; a line with no record simply has no chip.
  boxItems: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();
const { formatAmount } = useCurrency();

const itemsById = computed(() => {
  const map = new Map();
  for (const item of props.boxItems ?? []) {
    if (item?.id != null) map.set(String(item.id), item);
  }
  return map;
});

function groupHeader(group) {
  const parts = [group.supplierName || t('box.totals.account.items.manualGroup')];
  if ((group.poolShareCents ?? 0) > 0) {
    parts.push(group.ratePct != null
      ? t('box.totals.account.items.groupRate', { rate: group.ratePct })
      : t('box.totals.account.items.groupPlain'));
    parts.push(formatAmount(group.poolShareCents));
  }
  return parts.join(' · ');
}

const decoratedGroups = computed(() => orderedItemGroups(props.account).map((group) => ({
  ...group,
  headerText: groupHeader(group),
  decoratedLines: (Array.isArray(group.lines) ? group.lines : []).map((line) => {
    const item = itemsById.value.get(String(line.orderBoxItemId)) ?? null;
    if (!item) return { line, item: null, issue: null };
    const issue = classifyItemIssue(item);
    return { line, item, issue: { ...issue, chipVariant: issueChipVariant(issue) } };
  }),
})));

const lineCount = computed(() => props.account?.header?.lineCount
  ?? decoratedGroups.value.reduce((sum, group) => sum + group.decoratedLines.length, 0));

function qtyTimesUnit(line) {
  return t('box.totals.account.items.qtyTimesUnit', {
    qty: line.qty ?? 0,
    unit: formatAmount(line.unitCents),
  });
}
</script>

<style scoped>
.cis {
    padding: 12px 16px;
    border-bottom: 1px solid var(--poolit-border-color);
}

.cis-heading {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 12px;
    font-weight: var(--poolit-font-weight-semibold);
    color: var(--poolit-primary-600);
    letter-spacing: 0.4px;
    margin-bottom: 4px;
}

.cis-count {
    color: var(--poolit-text-secondary);
    font-feature-settings: 'tnum' on, 'lnum' on;
}

.cis-group + .cis-group {
    margin-top: 4px;
}

.cis-group-header {
    margin: 8px -16px 0;
    padding: 6px 16px;
    background: var(--poolit-bg-secondary, #f8fafc);
    font-size: 12px;
    font-weight: var(--poolit-font-weight-bold);
    color: var(--poolit-text-secondary);
    overflow-wrap: anywhere;
}

.cis-line {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid var(--poolit-border-color);
}

.cis-group .cis-line:last-child {
    border-bottom: 0;
}

.cis-line-main {
    min-width: 0;
}

.cis-line-name {
    font-size: 14px;
    color: var(--poolit-text-primary);
    overflow-wrap: anywhere;
}

.cis-line-sub {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
    font-size: 12px;
    color: var(--poolit-text-tertiary);
}

.cis-line-qty {
    font-feature-settings: 'tnum' on, 'lnum' on;
}

.cis-cash-chip {
    display: inline-flex;
    padding: 0 6px;
    border-radius: 4px;
    background: var(--poolit-success-50, #ecfdf5);
    color: var(--poolit-success-700, #047857);
    font-size: 11px;
}

.cis-line-after {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    flex-shrink: 0;
}

.cis-line-total {
    font-size: 14px;
    font-weight: var(--poolit-font-weight-semibold);
    color: var(--poolit-text-primary);
    font-feature-settings: 'tnum' on, 'lnum' on;
    white-space: nowrap;
}
</style>
